// =============================================================================
// ui/state/demo.tsx — demo mode: is it on, and what does the app read while it is.
// -----------------------------------------------------------------------------
// Relocated from the old client's `enterDemoMode` / `exitDemoMode`
// (app/index.html:8347-8412), with one structural difference that follows from
// this client's shape rather than from taste.
//
// The old client holds one `state` object and swaps it. This client has no such
// object — it reads the database in ~20 io modules — so what gets swapped here
// is the CLIENT (see `io/demo/client.ts`). Every provider takes `sb` as a prop
// and keys its effect on it, so changing the client's identity reloads the
// entire app by construction: entering and leaving a demo needs no per-provider
// wiring, and there is no provider anyone can forget.
//
// ── 🚨 `useDemo()` DOES NOT THROW WITHOUT A PROVIDER, UNLIKE EVERY OTHER ──
//
// `useAccounts()` and friends throw, because a screen reading data outside its
// provider is a bug that should be loud. A MODE FLAG is the opposite: absence
// means "not in a demo", which is both true and the safe direction, and making
// it throw would force every screen that so much as consults the flag — the
// free-tier caps, for one — to mount a provider in its own tests. D234 records
// exactly that trap costing eight tests in one edit.
//
// ── ⚠ THE ONE THING THE OLD CLIENT GUARDS THAT THIS DOES NOT NEED ──
//
// Its enter path is re-entrant: it flushes pending saves first, so a second
// click during the flush could capture the ALREADY-SWAPPED state as "real" and
// drop the user's book on the floor. There is nothing to flush here — writes go
// straight through and there is no held copy of anything — so entering twice is
// idempotent rather than dangerous.
// =============================================================================

import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { demoClient } from '../../io/demo/client';

export interface DemoState {
  active: boolean;
  /**
   * The client to read through, or null when no provider is mounted.
   *
   * ⚠ Null does NOT mean "use nothing" — it means the caller keeps the real
   * client it already had. See `App.tsx`.
   */
  client: SupabaseClient | null;
  enter: () => void;
  exit: () => void;
}

/** ⚠ Not in a demo, and doing nothing. See the header for why this is not a throw. */
const OFF: DemoState = { active: false, client: null, enter: () => {}, exit: () => {} };

const Ctx = createContext<DemoState>(OFF);

export function useDemo(): DemoState { return useContext(Ctx); }

export function DemoProvider(
  { real, userId, today, children }: {
    real: SupabaseClient;
    userId: string;
    /** Today, as an ISO date. Passed in so the sample window is testable. */
    today: string;
    children: ReactNode;
  },
) {
  const [active, setActive] = useState(false);

  // ⚠ MEMOISED ON THE INPUTS, so the client's identity is STABLE while the demo
  // is up. Every provider keys its load effect on `sb`; a fresh client per
  // render would re-read the whole app on every keystroke.
  const client = useMemo(
    () => (active ? demoClient(real, { today, userId }) : null),
    [active, real, today, userId],
  );

  const enter = useCallback(() => setActive(true), []);
  const exit = useCallback(() => setActive(false), []);

  const value = useMemo<DemoState>(
    () => ({ active, client, enter, exit }),
    [active, client, enter, exit],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
